import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import importAsset from 'lumen/helpers/import-asset';
import type Highlight from 'lumen/models/roi-trophies/highlight';
import type { ProductLifetimeData } from 'lumen/models/roi-trophies/highlight';
import lottieAnimation from 'lumen/modifiers/lottie-animation';

import GeneralRoiTrophiesContentTrophyModalLayout from './trophy-modal-layout';

interface GeneralRoiTrophiesContentProductLifetimeSignature {
  Args: {
    highlight: Highlight;
  };
}

export default class GeneralRoiTrophiesContentProductLifetime extends Component<GeneralRoiTrophiesContentProductLifetimeSignature> {
  @service declare intl: Services['intl'];

  get body(): string {
    const { months, pickup } = this.data;

    if (!pickup) {
      if (!months || months < 12) {
        return this.intl.t(
          'roi_trophies.product_lifetime.body_6_months_no_pickup',
        );
      }

      if (months < 24) {
        return this.intl.t(
          'roi_trophies.product_lifetime.body_1_year_no_pickup',
        );
      }

      return this.intl.t(
        'roi_trophies.product_lifetime.body_multi_year_no_pickup',
        {
          years: this.years,
        },
      );
    }

    if (!months || months < 12) {
      return this.intl.t('roi_trophies.product_lifetime.body_6_months', {
        pickup,
      });
    }

    if (months < 24) {
      return this.intl.t('roi_trophies.product_lifetime.body_1_year', {
        pickup,
      });
    }

    return this.intl.t('roi_trophies.product_lifetime.body_multi_year', {
      years: this.years,
      pickup,
    });
  }

  get data(): ProductLifetimeData {
    return this.args.highlight.highlightData as ProductLifetimeData;
  }

  get heading(): string {
    const { months } = this.data;

    if (!months || months < 12) {
      return this.intl.t('roi_trophies.product_lifetime.heading_6_months');
    }

    if (months < 24) {
      return this.intl.t('roi_trophies.product_lifetime.heading_1_year');
    }

    return this.intl.t('roi_trophies.product_lifetime.heading_multi_year', {
      years: this.years,
    });
  }

  private get years(): number {
    return Math.floor((this.data.months ?? 0) / 12);
  }

  <template>
    <GeneralRoiTrophiesContentTrophyModalLayout>
      <:content>
        <div class="flex flex-col items-center gap-300">
          <div
            class="heading-1"
            data-test-id="product-lifetime-heading"
          >{{this.heading}}</div>
          <p
            class="text-300--regular text-neutral-subdued"
            data-test-id="product-lifetime-body"
          >{{this.body}}</p>
        </div>
      </:content>
      <:animation>
        <div
          {{lottieAnimation
            (importAsset "animations/roi-trophies/anniversary.json")
            false
          }}
        />
      </:animation>
    </GeneralRoiTrophiesContentTrophyModalLayout>
  </template>
}
